/**
 * Unified Task Service - Phase 4 Architecture Modernization
 * Consolidates PersonalTasksView and ProfessionalTodoList functionality
 * Based on architectural-consolidation-plan.md
 */

import type { Database } from "@/types/database/base";
import { CreateTodoRequest, Todo, UpdateTodoRequest } from "@/types/todo";
import type { SupabaseClient } from "@supabase/supabase-js";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "./service-registry";
import { TodoService } from "./todo.service"; // Import the class directly
import { logger } from "@/lib/logger";
import { getErrorMessage } from "@/utils/serviceResponse";

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  completed: number;
  overdue: number;
  completedToday: number;
  completedThisWeek: number;
  completedThisMonth: number;
}

export interface TaskFilters {
  searchTerm?: string;
  statusFilter?: "all" | "todo" | "in_progress" | "completed";
  priorityFilter?: "all" | "high" | "medium" | "low";
  includeCompleted?: boolean;
  assignedTo?: string;
  createdBy?: string;
}

export interface TaskWithSubtasks extends Todo {
  subtasks?: Todo[];
  subtaskCount?: number;
  completedSubtaskCount?: number;
}

export interface TaskContext {
  mode: "personal" | "admin";
  userId?: string;
  showAssignmentControls?: boolean;
}

/**
 * Unified Task Service Interface
 * Provides consistent task management across profile and admin contexts
 */
export interface IUnifiedTaskService extends IService {
  // Core CRUD operations
  getTasks(
    context: TaskContext,
    filters?: TaskFilters,
    useCache?: boolean
  ): Promise<ServiceResponse<TaskWithSubtasks[]>>;
  createTask(task: CreateTodoRequest): Promise<ServiceResponse<Todo>>;
  updateTask(
    id: string,
    updates: UpdateTodoRequest
  ): Promise<ServiceResponse<Todo>>;
  deleteTask(id: string): Promise<ServiceResponse<boolean>>;

  // Subtask operations
  getSubtasks(parentId: string): Promise<ServiceResponse<Todo[]>>;
  createSubtask(
    parentId: string,
    subtask: Omit<CreateTodoRequest, "parent_id">
  ): Promise<ServiceResponse<Todo>>;

  // Statistics and analytics
  getTaskStats(userId?: string): Promise<ServiceResponse<TaskStats>>;

  // Profile-specific methods
  getPersonalTasks(
    userId: string,
    filters?: TaskFilters
  ): Promise<ServiceResponse<TaskWithSubtasks[]>>;

  // Admin-specific methods
  getAllTasks(
    filters?: TaskFilters
  ): Promise<ServiceResponse<TaskWithSubtasks[]>>;
  assignTask(
    taskId: string,
    assignedTo: string
  ): Promise<ServiceResponse<Todo>>;

  // Assignment operations
  handleAssignmentChange(task: Todo): Promise<ServiceResponse<void>>;

  // Cache management
  invalidateCache(): void;
  getCacheStats(): Record<string, unknown>;
}

/**
 * Unified Task Service Implementation
 * Consolidates task management functionality from multiple components
 */
export class UnifiedTaskService implements IUnifiedTaskService {
  readonly name = "unified-tasks";

  private cache = new Map<
    string,
    { data: unknown; timestamp: number; ttl: number }
  >();
  private readonly CACHE_TTL = 30000; // 30 seconds

  constructor(
    private supabase: SupabaseClient<Database>,
    private authService: {
      getCurrentUser(): Promise<{ id: string } | undefined>;
    },
    private todoService: TodoService // Inject TodoService
  ) {}

  async initialize(): Promise<void> {
    logger.info("🚀 UnifiedTaskService initialized");
  }

  async dispose(): Promise<void> {
    this.cache.clear();
    logger.info("🧹 UnifiedTaskService disposed");
  }

  /**
   * Get tasks with context-aware filtering
   */
  async getTasks(
    context: TaskContext,
    filters: TaskFilters = {},
    useCache = true
  ): Promise<ServiceResponse<TaskWithSubtasks[]>> {
    const startTime = Date.now();
    const cacheKey = this.getCacheKey(context, filters);

    try {
      // Check cache first
      if (useCache) {
        const cached = this.getCachedData<TaskWithSubtasks[]>(cacheKey);
        if (cached) {
          logger.info(`📦 Task cache hit for ${cacheKey}`);
          return createSuccessResponse(cached, {
            timestamp: new Date(),
            duration: Date.now() - startTime,
            cached: true,
          });
        }
      }

      // Fetch tasks based on context
      let tasks: Todo[] = [];

      if (context.mode === "personal" && context.userId) {
        const personalTasksResult = await this.getPersonalTasksInternal(
          context.userId,
          filters
        );
        tasks = personalTasksResult;
      } else {
        tasks = await this.getAdminTasks(filters);
      }

      // Enhance with subtasks
      const tasksWithSubtasks = await this.enrichWithSubtasks(tasks);

      // Cache the results
      this.setCachedData(cacheKey, tasksWithSubtasks);

      return createSuccessResponse(tasksWithSubtasks, {
        timestamp: new Date(),
        duration: Date.now() - startTime,
        cached: false,
      });
    } catch (error) {
      logger.error("Error fetching tasks:", { error: error });
      return createErrorResponse(
        error instanceof Error ? error.message : "Failed to fetch tasks"
      );
    }
  }

  /**
   * Get personal tasks (public method implementation)
   */
  async getPersonalTasks(
    userId: string,
    filters: TaskFilters = {}
  ): Promise<ServiceResponse<TaskWithSubtasks[]>> {
    const startTime = Date.now();

    try {
      const tasks = await this.getPersonalTasksInternal(userId, filters);
      const tasksWithSubtasks = await this.enrichWithSubtasks(tasks);

      return createSuccessResponse(tasksWithSubtasks, {
        timestamp: new Date(),
        duration: Date.now() - startTime,
        cached: false,
      });
    } catch (error) {
      logger.error("Error fetching personal tasks:", { error: error });
      return createErrorResponse(
        error instanceof Error ? error.message : "Failed to fetch personal tasks"
      );
    }
  }

  /**
   * Get personal tasks (for profile view) - Internal method
   */
  private async getPersonalTasksInternal(
    userId: string,
    filters: TaskFilters,
    organizationId?: string
  ): Promise<Todo[]> {
    const response = await this.todoService.getTodos(
      filters.includeCompleted ?? false,
      organizationId
    );
    if (response.error) {throw new Error(getErrorMessage(response.error));}
    const allTasks = response.data ?? [];

    // Filter for personal relevance:
    // 1. Tasks assigned TO the user (assigned_to === userId)
    // 2. Tasks created BY the user that are NOT assigned to others
    const relevance = {
      isAssignedToMe: false,
      isCreatorAndUnassigned: false,
    };

    const filteredTasks = allTasks.filter((task: Todo) => {
      // Tasks assigned to me
      if (task.assigned_to === userId) {
        relevance.isAssignedToMe = true;
        return true;
      }
      // Tasks I created that are either unassigned or assigned to me
      if (
        task.created_by === userId &&
        (!task.assigned_to || task.assigned_to === userId)
      ) {
        relevance.isCreatorAndUnassigned = true;
        return true;
      }
      return false;
    });

    return filteredTasks.filter((task: Todo) => this.applyFilters(task, filters));
  }

  /**
   * Get admin tasks (for admin view)
   */
  private async getAdminTasks(filters: TaskFilters): Promise<Todo[]> {
    const response = await this.todoService.getFilteredTodos({
      ...filters,
      parentOnly: false,
    });
    if (response.error) {throw new Error(getErrorMessage(response.error));}
    return response.data ?? [];
  }

  /**
   * Apply filters to tasks
   */
  private applyFilters(task: Todo, filters: TaskFilters): boolean {
    const {
      searchTerm = "",
      statusFilter = "all",
      priorityFilter = "all",
      assignedTo,
      createdBy,
    } = filters;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        task.title.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower);
      if (!matchesSearch) {return false;}
    }

    // Status filter
    if (statusFilter !== "all" && task.status !== statusFilter) {
      return false;
    }

    // Priority filter
    if (priorityFilter !== "all" && task.priority !== priorityFilter) {
      return false;
    }

    // Assignment filters
    if (assignedTo && task.assigned_to !== assignedTo) {
      return false;
    }

    if (createdBy && task.created_by !== createdBy) {
      return false;
    }

    return true;
  }

  /**
   * Enrich tasks with subtask information
   */
  private async enrichWithSubtasks(tasks: Todo[]): Promise<TaskWithSubtasks[]> {
    return Promise.all(
      tasks.map(async (task): Promise<TaskWithSubtasks> => {
        if (!task.parent_id) {
          // Only fetch subtasks for parent tasks
          try {
            const response = await this.todoService.getSubtasks(task.id);
            const subtasks = response.data ?? [];
            return {
              ...task,
              subtasks,
              subtaskCount: subtasks.length,
              completedSubtaskCount: subtasks.filter(
                (st) => st.status === "completed"
              ).length,
            };
          } catch (error) {
            logger.warn(
              `Failed to fetch subtasks for task ${task.id}:`, { error: error }
            );
            return {
              ...task,
              subtasks: [],
              subtaskCount: 0,
              completedSubtaskCount: 0,
            };
          }
        }
        return task as TaskWithSubtasks;
      })
    );
  }

  /**
   * Calculate task statistics
   */
  calculateStats(tasks: TaskWithSubtasks[]): TaskStats {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Include subtasks in statistics
    const allTasksIncludingSubtasks = tasks.reduce<Todo[]>((acc, task) => {
      acc.push(task);
      if (task.subtasks) {
        acc.push(...task.subtasks);
      }
      return acc;
    }, []);

    return {
      total: allTasksIncludingSubtasks.length,
      todo: allTasksIncludingSubtasks.filter((t) => t.status === "todo").length,
      inProgress: allTasksIncludingSubtasks.filter(
        (t) => t.status === "in_progress"
      ).length,
      completed: allTasksIncludingSubtasks.filter(
        (t) => t.status === "completed"
      ).length,
      overdue: allTasksIncludingSubtasks.filter(
        (t) =>
          t.due_date && new Date(t.due_date) < now && t.status !== "completed"
      ).length,
      completedToday: allTasksIncludingSubtasks.filter(
        (t) =>
          t.status === "completed" &&
          t.updated_at &&
          new Date(t.updated_at) >= today
      ).length,
      completedThisWeek: allTasksIncludingSubtasks.filter(
        (t) =>
          t.status === "completed" &&
          t.updated_at &&
          new Date(t.updated_at) >= weekAgo
      ).length,
      completedThisMonth: allTasksIncludingSubtasks.filter(
        (t) =>
          t.status === "completed" &&
          t.updated_at &&
          new Date(t.updated_at) >= monthAgo
      ).length,
    };
  }

  /**
   * Create a new task
   */
  async createTask(task: CreateTodoRequest): Promise<ServiceResponse<Todo>> {
    const currentUser = await this.authService.getCurrentUser();
    if (!currentUser) {
      return createErrorResponse("User not authenticated");
    }

    return await this.todoService.createTodo(task);
  }

  /**
   * Update a task
   */
  async updateTask(
    id: string,
    updates: UpdateTodoRequest
  ): Promise<ServiceResponse<Todo>> {
    const currentUser = await this.authService.getCurrentUser();
    if (!currentUser) {
      return createErrorResponse("User not authenticated");
    }
    return await this.todoService.updateTodo(id, updates);
  }

  /**
   * Delete a task
   */
  async deleteTask(id: string): Promise<ServiceResponse<boolean>> {
    const response = await this.todoService.deleteTodo(id);
    if (!response.success) {
      return createErrorResponse(getErrorMessage(response.error));
    }
    return createSuccessResponse(true);
  }

  /**
   * Get subtasks for a parent task
   */
  async getSubtasks(parentId: string): Promise<ServiceResponse<Todo[]>> {
    const startTime = Date.now();
    const cacheKey = `subtasks:${parentId}`;

    try {
      // Check cache first
      const cached = this.getCachedData<Todo[]>(cacheKey);
      if (cached) {
        return createSuccessResponse(cached, {
          timestamp: new Date(),
          duration: Date.now() - startTime,
          cached: true,
        });
      }

      const response = await this.todoService.getSubtasks(parentId);
      if (response.data) {
        this.setCachedData(cacheKey, response.data);
      }
      return response;
    } catch (error) {
      logger.error(`Error fetching subtasks for ${parentId}:`, { error: error });
      return createErrorResponse(
        error instanceof Error ? error.message : "Failed to fetch subtasks"
      );
    }
  }

  /**
   * Create a subtask
   */
  async createSubtask(
    parentId: string,
    subtask: Omit<CreateTodoRequest, "parent_id">
  ): Promise<ServiceResponse<Todo>> {
    return await this.todoService.createSubtask(parentId, subtask);
  }

  /**
   * Get task statistics
   */
  async getTaskStats(userId?: string, organizationId?: string): Promise<ServiceResponse<TaskStats>> {
    const startTime = Date.now();
    try {
      const response = await this.todoService.getTodos(true, organizationId);
      if (response.error) {throw new Error(getErrorMessage(response.error));}
      const tasks = response.data ?? [];

      const userTasks = userId
        ? tasks.filter((task) => task.created_by === userId)
        : tasks;
      const stats = this.calculateStats(
        await this.enrichWithSubtasks(userTasks)
      );
      return createSuccessResponse(stats, {
        timestamp: new Date(),
        duration: Date.now() - startTime,
        cached: false,
      });
    } catch (error) {
      return createErrorResponse(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }

  /**
   * Get all tasks (admin view)
   */
  async getAllTasks(
    filters?: TaskFilters
  ): Promise<ServiceResponse<TaskWithSubtasks[]>> {
    return this.getTasks({ mode: "admin" }, filters);
  }

  /**
   * Assign a task to a user
   */
  async assignTask(
    taskId: string,
    assignedTo: string
  ): Promise<ServiceResponse<Todo>> {
    const response = await this.updateTask(taskId, { assigned_to: assignedTo });
    if (!response.success) {return response;}

    // In a real implementation, you might want to send a notification here

    return response;
  }

  /**
   * Handle assignment changes (for compatibility)
   */
  async handleAssignmentChange(_task: Todo): Promise<ServiceResponse<void>> {
    // Invalidate cache to refresh data
    this.invalidateCache();
    return createSuccessResponse(undefined);
  }

  /**
   * Cache management
   */
  invalidateCache(): void {
    this.cache.clear();
  }

  getCacheStats(): Record<string, unknown> {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      ages: Array.from(this.cache.values()).map(
        (v) => Date.now() - v.timestamp
      ),
    };
  }

  // Private helper methods
  private getCachedData<T>(key: string): T | undefined {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data as T;
    }
    return undefined;
  }

  private setCachedData<T>(key: string, data: T, ttl = this.CACHE_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  private getCacheKey(context: TaskContext, filters: TaskFilters): string {
    const contextKey = `${context.mode}-${context.userId ?? "all"}`;
    const filtersKey = JSON.stringify(filters);
    return `${contextKey}-${filtersKey}`;
  }
}
